import { HHStack } from '@hinge-health/react-component-library';
import { useEffect } from 'react';
import { useBillingSetting } from '../../context/billing.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useGetBillDetailsByIdQuery } from '../../types';
import DetailsAccordion from '../details-accordion';
import EngagementDetails from '../engagement/engagement-details';
import JsonAccordion from '../json-accordion';
import LoadingComponent from '../utils/loading';
import BillDrawerDetails from './drawer-components/bill-drawer-details';
import HoldDetails from './drawer-components/hold-details';
import PriceAdjustmentDetails from './drawer-components/price-adjustment-details';
import SubmissionDetails from './drawer-components/submission-details';
import { processPriceAdjustments } from './utils';

interface BillDetailsProps {
  billId: number;
  currency: string | null;
}

const BillDetails = ({ billId, currency }: BillDetailsProps): JSX.Element => {
  const { value: displayLocal } = useBillingSetting('displayDateTimeLocal');
  const {
    data: billDetails,
    error,
    loading,
  } = useGetBillDetailsByIdQuery({
    variables: {
      billId,
    },
  });
  const { setSnackbarState } = useSnackbarContext();

  useEffect(() => {
    if (error && !loading) {
      setSnackbarState('Error loading additional bill details', 'error', 4000);
    }
  }, [error, loading, setSnackbarState]);

  const { adjustmentEntries, isBillAmountValid } = billDetails?.getBillById
    ? processPriceAdjustments(billDetails.getBillById)
    : { adjustmentEntries: [], isBillAmountValid: true };

  if (loading) {
    return <LoadingComponent center />;
  }

  return (
    <HHStack padding={1}>
      {billDetails?.getBillById && (
        <BillDrawerDetails
          billDetails={billDetails.getBillById}
          currency={currency}
          displayLocal={displayLocal}
          isBillAmountValid={isBillAmountValid}
        />
      )}
      {billDetails?.getBillById?.details?.costShare && (
        <JsonAccordion
          data={billDetails.getBillById.details.costShare}
          title="Cost Share Details"
        />
      )}
      {billDetails?.getBillById?.details && (
        <DetailsAccordion title="Price Adjustment">
          <PriceAdjustmentDetails
            adjustmentEntries={adjustmentEntries}
            isBillAmountValid={isBillAmountValid}
            baseCharge={billDetails?.getBillById?.baseCharge ?? 0}
            billAmount={billDetails?.getBillById?.billAmount ?? 0}
            currency={currency}
          />
        </DetailsAccordion>
      )}
      {billDetails?.getBillById.engagementEvent && (
        <DetailsAccordion title="Engagement Event">
          <EngagementDetails
            engagement={billDetails.getBillById.engagementEvent}
          />
        </DetailsAccordion>
      )}
      {billDetails?.getBillById.internalHolds && (
        <DetailsAccordion title="Hold Details">
          <HoldDetails
            internalHoldsDetails={billDetails?.getBillById.internalHolds}
          />
        </DetailsAccordion>
      )}
      {billDetails?.getBillById.submissions && (
        <DetailsAccordion title="Submission Details">
          <SubmissionDetails
            submissionsDetails={billDetails?.getBillById.submissions}
            displayLocal={displayLocal}
            currency={currency}
          />
        </DetailsAccordion>
      )}
    </HHStack>
  );
};

export default BillDetails;
